import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IntakeColumnMappingProposal } from '../intake.api';

export interface ProfileAppliedBannerProps {
  columns: IntakeColumnMappingProposal[];
}

/**
 * h5-7 (FR35): "Uw eerdere voorkeuren zijn toegepast op N kolommen" — a positive confirmation,
 * not a warning, so DS success-green (never info-blue or red/amber) per the design system's
 * colour semantics. Renders nothing when no column was profile-sourced this run.
 */
export function ProfileAppliedBanner({ columns }: ProfileAppliedBannerProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const profileColumns = columns.filter((column) => column.source === 'profile');

  if (profileColumns.length === 0) return null;

  return (
    <div
      role="status"
      data-testid="intake-profile-applied-banner"
      className="rounded-[var(--radius-card)] border border-[var(--success)] bg-[var(--success-light)] p-3 text-[var(--success-dark)]"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium">{t('intake.review.profileApplied.title', { count: profileColumns.length })}</p>
        <button
          type="button"
          data-testid="intake-profile-applied-banner-toggle"
          onClick={() => setExpanded((value) => !value)}
          className="text-xs underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--secondary)]"
        >
          {expanded ? t('intake.review.profileApplied.collapse') : t('intake.review.profileApplied.expand')}
        </button>
      </div>
      {expanded ? (
        <ul className="mt-1 list-inside list-disc text-sm" data-testid="intake-profile-applied-banner-list">
          {profileColumns.map((column) => (
            <li key={column.header}>{column.header}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
